/**
 * Public API documentation. Human-readable quickstart + a link to the live
 * OpenAPI spec (importable into Postman/codegen). Kept in sync with
 * backend_cf/src/lib/openapi.ts by hand.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { API_BASE as API } from '../config';

const curlExample = `curl -X POST ${'API_BASE'}/v1/generate \\
  -H "X-API-Key: vk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "job_title": "Platform Engineer",
    "company": "CloudCo",
    "job_description": "We need Kubernetes, Terraform, AWS...",
    "options": { "fit": 85, "anonymity": 0, "length": 50, "tone": "impact" }
  }'`;

const Code: React.FC<{ children: string }> = ({ children }) => (
  <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-4 overflow-auto">{children}</pre>
);

const ApiDocsPage: React.FC = () => {
  const base = API;
  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Veritas API</h1>
        <Link to="/app/settings" className="text-sm text-blue-600 hover:underline">Manage keys →</Link>
      </div>

      <p className="text-gray-600 mb-6">
        Generate truth-checked, tunable CVs from a candidate profile. Every claim in the output is
        classified <b>verifiable / inferred / fabricated</b> against the source profile — the generator
        never invents facts. Base URL: <code className="bg-gray-100 px-1 rounded">{base}</code>
      </p>

      <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-2">1. Authentication</h2>
      <p className="text-gray-600 mb-3">
        Create a key in <Link to="/app/settings" className="text-blue-600 hover:underline">Settings → API keys</Link>,
        then send it as the <code className="bg-gray-100 px-1 rounded">X-API-Key</code> header. Verify it:
      </p>
      <Code>{`curl ${base}/v1/ping -H "X-API-Key: vk_live_..."`}</Code>

      <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-2">2. Generate a CV</h2>
      <p className="text-gray-600 mb-3">
        <code className="bg-gray-100 px-1 rounded">POST /v1/generate</code>. Uses your saved profile by
        default, or pass a <code className="bg-gray-100 px-1 rounded">profile</code> object inline to
        generate for someone else (great for coaches / other apps).
      </p>
      <Code>{curlExample.replace('API_BASE', base)}</Code>

      <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-2">3. Tuning knobs (<code>options</code>)</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-gray-200 rounded-lg">
          <thead className="bg-gray-50 text-gray-600">
            <tr><th className="text-left p-2">Field</th><th className="text-left p-2">Range</th><th className="text-left p-2">Effect</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr><td className="p-2 font-mono">fit</td><td className="p-2">0–100</td><td className="p-2">How aggressively to tailor toward the job description.</td></tr>
            <tr><td className="p-2 font-mono">anonymity</td><td className="p-2">0–100</td><td className="p-2">Deterministic redaction: ≥15 contact, ≥35 name→initials, ≥55 employers, ≥75 schools, ≥90 dates→durations.</td></tr>
            <tr><td className="p-2 font-mono">length</td><td className="p-2">0–100</td><td className="p-2">0 = concise one-pager, 100 = detailed.</td></tr>
            <tr><td className="p-2 font-mono">tone</td><td className="p-2">enum</td><td className="p-2">professional · impact · concise · warm</td></tr>
          </tbody>
        </table>
      </div>

      <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-2">4. Response</h2>
      <p className="text-gray-600 mb-3">Returns the structured CV, LaTeX source, cover letter, the truth report, and the resolved options.</p>
      <Code>{`{
  "tailored": { "tailored_summary": "...", "skills_focus": [...], "experience": [...], "cover_letter": "..." },
  "latex": "\\\\documentclass...",
  "cover_letter": "...",
  "truth_report": { "score": 88, "counts": {...}, "claims": [...] },
  "options": { "fit": 85, "anonymity": 0, "length": 50, "tone": "impact" }
}`}</Code>

      <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-2">OpenAPI spec</h2>
      <p className="text-gray-600">
        Machine-readable spec (import into Postman, Insomnia, or a codegen tool):{' '}
        <a href={`${base}/v1/openapi.json`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
          {base}/v1/openapi.json
        </a>
      </p>

      <p className="text-xs text-gray-400 mt-10">
        The honesty guarantee holds through the API: no knob relaxes the anti-fabrication rule.
      </p>
    </div>
  );
};

export default ApiDocsPage;
